import { useState, useCallback, useEffect, useMemo } from 'react'
import { X, Trash2, Plus, AlertTriangle, Loader2, Copy } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerCell } from '@/components/common/DatePickerCell'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { ReadOnlyBanner } from '@/components/common/ReadOnlyBanner'
import { useModelGroups } from '@/hooks/useStoreModels'
import {
  useMultiloc,
  useCreateMultiloc,
  useUpdateMultiloc,
  useDeleteMultiloc,
  useApplyMultilocToAll,
} from '@/hooks/useMultiloc'
import type { PlanDetailItem } from '@/types'
import { toast } from '@/stores/useToastStore'
import { cn } from '@/utils/cn'
import {
  createEmptyRow,
  mapApiToRows,
  mapRowsToCreateRequest,
  mapRowsToUpdateRequest,
  DEFAULT_GROUP_SENTINEL,
  ALL_GROUPS_SENTINEL,
} from './types'
import type { MultilocRow } from './types'
import { CopyMultilocDialog } from './CopyMultilocDialog'

interface MultilocDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: PlanDetailItem | null
  planId: string
  versionId: string
  isReadOnly?: boolean
}

export function MultilocDrawer({
  open,
  onOpenChange,
  item,
  planId,
  versionId,
  isReadOnly = false,
}: MultilocDrawerProps) {
  const [rows, setRows] = useState<MultilocRow[]>([createEmptyRow()])
  const [isExistingData, setIsExistingData] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [applyToAll, setApplyToAll] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const { data: groupsData, isLoading: isLoadingGroups } = useModelGroups(
    item?.storeModelId ?? undefined,
    ''
  )

  const { data: multilocData, isLoading: isLoadingMultiloc } = useMultiloc(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? item?.id : undefined
  )

  const createMutation = useCreateMultiloc(planId, versionId)
  const updateMutation = useUpdateMultiloc(planId, versionId)
  const deleteMutation = useDeleteMultiloc(planId, versionId)
  const applyToAllMutation = useApplyMultilocToAll(planId, versionId)

  const hasStoreModel = !!item?.storeModelId
  const allModelGroups = useMemo(() => groupsData?.data ?? [], [groupsData])
  const groups = useMemo(() => {
    const activeIds = item?.activeStoreGroupIds ?? []
    if (activeIds.length === 0) return allModelGroups
    const activeSet = new Set(activeIds)
    return allModelGroups.filter(g => activeSet.has(g.id))
  }, [allModelGroups, item?.activeStoreGroupIds])
  const hasNoGroups = !isLoadingGroups && groups.length === 0
  const hasAllGroupsRow = rows.some(r => r.storeGroupId === ALL_GROUPS_SENTINEL)
  const isLoading = isLoadingGroups || isLoadingMultiloc
  const hasMultiloc = (multilocData?.items.length ?? 0) > 0
  const showEmptyReadOnly = isReadOnly && !hasMultiloc
  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    applyToAllMutation.isPending

  // Populate rows from fetched data when drawer opens
  useEffect(() => {
    if (!open || isLoadingMultiloc) return

    if (multilocData && multilocData.items.length > 0) {
      setRows(mapApiToRows(multilocData.items))
      setIsExistingData(true)
    } else {
      setRows([createEmptyRow()])
      setIsExistingData(false)
    }
  }, [open, multilocData, isLoadingMultiloc])

  // Auto-select "Default" group when store model has no groups or no store model
  useEffect(() => {
    if (!hasNoGroups && hasStoreModel) return
    setRows(prev =>
      prev.map(r =>
        r.storeGroupId === ''
          ? { ...r, storeGroupId: DEFAULT_GROUP_SENTINEL }
          : r
      )
    )
  }, [hasNoGroups, hasStoreModel, rows.length])

  const handleAddRow = useCallback(() => {
    setRows(prev => [...prev, createEmptyRow()])
  }, [])

  const handleDeleteRow = useCallback((rowId: string) => {
    setRows(prev => {
      if (prev.length <= 1) return prev
      return prev.filter(r => r.id !== rowId)
    })
  }, [])

  const handleCopy = useCallback(
    (copiedRows: MultilocRow[]) => {
      const validGroupIds = new Set(
        hasNoGroups ? [DEFAULT_GROUP_SENTINEL] : groups.map(g => g.id)
      )
      setRows(
        copiedRows.map(row => ({
          ...row,
          backendId: null,
          storeGroupId: validGroupIds.has(row.storeGroupId)
            ? row.storeGroupId
            : '',
        }))
      )
    },
    [groups, hasNoGroups]
  )

  const handleRowChange = useCallback(
    (rowId: string, field: keyof MultilocRow, value: string | Date | null) => {
      setRows(prev => {
        const updated = prev.map(r =>
          r.id === rowId ? { ...r, [field]: value } : r
        )
        // When "All Groups" is selected, remove all other rows as they are redundant
        if (field === 'storeGroupId' && value === ALL_GROUPS_SENTINEL) {
          return updated.filter(r => r.id === rowId)
        }
        return updated
      })
    },
    []
  )

  const handleSave = useCallback(() => {
    if (!item) return

    const hasInvalidRows = rows.some(
      r =>
        !r.storeGroupId ||
        !r.presentationLocation.trim() ||
        !r.numberOfPresentations.trim() ||
        !r.numberOfFacings.trim() ||
        !r.setDate
    )

    if (hasInvalidRows) {
      setShowErrors(true)
      toast.error('Please fill in all required fields')
      return
    }

    const expandAllGroupsRows = (rowList: MultilocRow[]): MultilocRow[] =>
      rowList.flatMap(row =>
        row.storeGroupId === ALL_GROUPS_SENTINEL
          ? groups.map(g => ({
              ...row,
              id: crypto.randomUUID(),
              storeGroupId: g.id,
              backendId: null,
            }))
          : [row]
      )

    const finalRows = expandAllGroupsRows(rows)
    const lineId = item.id

    const onSaveSuccess = () => {
      if (applyToAll) {
        applyToAllMutation.mutate(lineId, {
          onSuccess: () => onOpenChange(false),
        })
      } else {
        onOpenChange(false)
      }
    }

    if (isExistingData) {
      updateMutation.mutate(
        { lineId, body: mapRowsToUpdateRequest(finalRows) },
        { onSuccess: onSaveSuccess }
      )
    } else {
      createMutation.mutate(
        { lineId, body: mapRowsToCreateRequest(finalRows) },
        { onSuccess: onSaveSuccess }
      )
    }
  }, [
    item,
    isExistingData,
    rows,
    groups,
    applyToAll,
    createMutation,
    updateMutation,
    applyToAllMutation,
    onOpenChange,
  ])

  const handleConfirmRemove = useCallback(() => {
    if (!item) return
    deleteMutation.mutate(
      { lineId: item.id },
      {
        onSuccess: () => {
          setConfirmRemoveOpen(false)
          onOpenChange(false)
        },
      }
    )
  }, [item, deleteMutation, onOpenChange])

  // Reset rows when drawer closes
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setRows([createEmptyRow()])
        setIsExistingData(false)
        setApplyToAll(false)
        setShowErrors(false)
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange]
  )

  if (!item) return null

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-[95vw] sm:max-w-[1100px] flex-col"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <SheetTitle className="text-xl font-bold">
                  {item.description || 'Untitled'}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Presentation model configuration
                </SheetDescription>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">
                    SKU {item.skuNumber || '-'}
                  </span>
                  <Badge variant="secondary">Presentation Model</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasStoreModel && !isReadOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer text-[#CF1F2E] hover:text-[#CF1F2E]/80"
                  onClick={() => setCopyDialogOpen(true)}
                >
                  <Copy className="h-4 w-4 mr-1.5" />
                  Copy from another SKU
                </Button>
              )}
              <SheetClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && showEmptyReadOnly && (
            <p className="text-sm text-muted-foreground text-center py-12">
              No presentation model defined.
            </p>
          )}

          {!isLoading && !showEmptyReadOnly && (
            <>
              {isReadOnly && (
                <ReadOnlyBanner className="mb-4">
                  View only. This plan is read-only, so the presentation model
                  can&apos;t be changed.
                </ReadOnlyBanner>
              )}

              {/* Warning banner when no store model or no groups */}
              {(!hasStoreModel || hasNoGroups) && (
                <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 mb-4">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    {!hasStoreModel
                      ? 'No Store Model assigned. Only one default location is available.'
                      : 'Store Group not assigned yet. Values will apply once assigned.'}
                  </span>
                </div>
              )}

              {/* Table */}
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Store Group *
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Location *
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Min. Pres*
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        # Facings *
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Lift %
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Set Date *
                      </th>
                      <th className="text-left px-2 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                        Exit Date
                      </th>
                      {!isReadOnly && (
                        <th className="w-10 sticky right-0 bg-muted/50 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.15)]" />
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.id} className="border-b last:border-0">
                        {/* Store Group */}
                        <td className="px-2 py-2">
                          <Select
                            value={row.storeGroupId}
                            onValueChange={val =>
                              handleRowChange(row.id, 'storeGroupId', val)
                            }
                            disabled={isLoadingGroups || isReadOnly}
                          >
                            <SelectTrigger
                              className={cn(
                                'h-9 min-w-[120px]',
                                showErrors &&
                                  !row.storeGroupId &&
                                  'border-red-500'
                              )}
                            >
                              {isLoadingGroups ? (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading...
                                </div>
                              ) : (
                                <SelectValue placeholder="Select" />
                              )}
                            </SelectTrigger>
                            <SelectContent>
                              {hasNoGroups ? (
                                <SelectItem value={DEFAULT_GROUP_SENTINEL}>
                                  Default
                                </SelectItem>
                              ) : (
                                <>
                                  <SelectItem value={ALL_GROUPS_SENTINEL}>
                                    All Groups
                                  </SelectItem>
                                  {groups.map(g => (
                                    <SelectItem key={g.id} value={g.id}>
                                      {g.name}
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </td>
                        {/* Presentation Location */}
                        <td className="px-2 py-2">
                          <Input
                            disabled={isReadOnly}
                            type="text"
                            className={cn(
                              'h-9 min-w-[120px]',
                              showErrors &&
                                !row.presentationLocation.trim() &&
                                'border-red-500'
                            )}
                            placeholder="Enter location"
                            value={row.presentationLocation}
                            onChange={e =>
                              handleRowChange(
                                row.id,
                                'presentationLocation',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        {/* Min. Pres*/}
                        <td className="px-2 py-2">
                          <Input
                            disabled={isReadOnly}
                            type="number"
                            className={cn(
                              'h-9 w-16',
                              showErrors &&
                                !row.numberOfPresentations.trim() &&
                                'border-red-500'
                            )}
                            placeholder="0"
                            value={row.numberOfPresentations}
                            onChange={e =>
                              handleRowChange(
                                row.id,
                                'numberOfPresentations',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        {/* # Facings */}
                        <td className="px-2 py-2">
                          <Input
                            disabled={isReadOnly}
                            type="number"
                            className={cn(
                              'h-9 w-16',
                              showErrors &&
                                !row.numberOfFacings.trim() &&
                                'border-red-500'
                            )}
                            placeholder="0"
                            value={row.numberOfFacings}
                            onChange={e =>
                              handleRowChange(
                                row.id,
                                'numberOfFacings',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        {/* Est. Lift % */}
                        <td className="px-2 py-2">
                          <Input
                            disabled={isReadOnly}
                            type="number"
                            className="h-9 w-16"
                            placeholder="0"
                            value={row.estLift}
                            onChange={e =>
                              handleRowChange(row.id, 'estLift', e.target.value)
                            }
                          />
                        </td>
                        {/* Set Date */}
                        <td className="px-2 py-2">
                          <DatePickerCell
                            disabled={isReadOnly}
                            value={row.setDate}
                            onChange={date =>
                              handleRowChange(row.id, 'setDate', date)
                            }
                            className={cn(
                              showErrors && !row.setDate && 'border-red-500'
                            )}
                          />
                        </td>
                        {/* Exit Date */}
                        <td className="px-2 py-2">
                          <DatePickerCell
                            disabled={isReadOnly}
                            value={row.exitDate}
                            onChange={date =>
                              handleRowChange(row.id, 'exitDate', date)
                            }
                          />
                        </td>
                        {/* Delete */}
                        {!isReadOnly && (
                          <td className="px-2 py-2 sticky right-0 bg-background shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.15)]">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={rows.length <= 1 || !hasStoreModel}
                              onClick={() => handleDeleteRow(row.id)}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Row */}
              {hasStoreModel && !hasAllGroupsRow && !isReadOnly && (
                <button
                  onClick={handleAddRow}
                  className="flex items-center gap-1 mt-3 text-sm font-medium text-[#CF1F2E] hover:text-[#CF1F2E]/80 cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </button>
              )}

              {/* Apply to All Lines */}
              {!isReadOnly && (
                <div className="border-t border-[#e7e7e7] pt-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="apply-multiloc-to-all"
                      checked={applyToAll}
                      onCheckedChange={checked =>
                        setApplyToAll(checked === true)
                      }
                      className="cursor-pointer mt-0.5"
                    />
                    <div>
                      <label
                        htmlFor="apply-multiloc-to-all"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Apply to all other lines in this plan
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Use this presentation model as a template for every line
                        item.
                      </p>
                    </div>
                  </div>
                  {applyToAll && (
                    <Alert variant="warning" className="mt-3">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        This will overwrite existing presentation models on all
                        other line items. This action cannot be undone.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div>
              {isExistingData && !isReadOnly && (
                <button
                  type="button"
                  className="text-sm text-destructive hover:text-destructive/80 underline cursor-pointer"
                  onClick={() => setConfirmRemoveOpen(true)}
                >
                  Remove Presentation Model
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {isReadOnly ? 'Close' : 'Cancel'}
              </Button>
              {!isReadOnly && (
                <Button
                  className="bg-[#CF1F2E] hover:bg-[#CF1F2E]/90 text-white"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isExistingData
                    ? 'Save Presentation Model'
                    : 'Create Presentation Model'}
                </Button>
              )}
            </div>
          </div>
        </SheetFooter>
      </SheetContent>

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove Presentation Model"
        description="Are you sure you want to remove the presentation model? This action cannot be undone."
        variant="destructive"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmRemove}
      />

      <CopyMultilocDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        planId={planId}
        versionId={versionId}
        currentLineId={item.id}
        onCopy={handleCopy}
      />
    </Sheet>
  )
}
